/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { C, t, fmt$, formatDate } from "../shared/globals.js";

function ChartContainer({ label, children, style = {} }) {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new ResizeObserver(es => {
      const w = es[0].contentRect.width;
      if (w > 0) setWidth(w);
    });
    obs.observe(ref.current);
    // Read immediately + after layout settles
    const measure = () => {
      if (ref.current) {
        const w = ref.current.getBoundingClientRect().width;
        if (w > 0) setWidth(w);
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(measure));
    const t = setTimeout(measure, 200);
    return () => { obs.disconnect(); clearTimeout(t); };
  }, []);

  return (
    <div ref={ref} style={{ flex:1, background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:10, padding:"16px 16px 8px", display:"flex", flexDirection:"column", minHeight:220, overflow:"hidden", minWidth:0, ...style }}>
      <div style={{ fontSize:11, fontWeight:700, letterSpacing:"0.3em", color:"#888", marginBottom:4, flexShrink:0, fontFamily:"Montserrat,sans-serif" }}>{label}</div>
      <div style={{ flex:1, minHeight:0 }}>
        {children(width)}
      </div>
    </div>
  );
}

function PortfolioChart({ entries, currentPrice, containerWidth }) {
  const svgRef      = useRef(null);
  const wrapRef     = useRef(null);
  const valueRef    = useRef(null);
  const investedRef = useRef(null);
  const avgRef      = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const tooltipHideTimer = useRef(null);
  const [dims, setDims] = useState({ w: containerWidth || 400, h: 200 });
  const [animated, setAnimated] = useState(false);
  const [zoomRange, setZoomRange] = useState([0, 1]);

  // If containerWidth provided externally, use it directly
  useEffect(() => {
    if (containerWidth > 0) setDims(d => ({ ...d, w: containerWidth }));
  }, [containerWidth]);

  useEffect(() => {
    if (containerWidth > 0) return; // skip if parent controls width
    const obs = new ResizeObserver((es) => {
      const { width, height } = es[0].contentRect;
      setDims({ w: Math.max(width, 100), h: Math.max(height, 80) });
    });
    if (wrapRef.current) {
      obs.observe(wrapRef.current);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (wrapRef.current) {
          const { width, height } = wrapRef.current.getBoundingClientRect();
          if (width > 0) setDims({ w: width, h: Math.max(height, 80) });
        }
      }));
      setTimeout(() => {
        if (wrapRef.current) {
          const { width, height } = wrapRef.current.getBoundingClientRect();
          if (width > 0) setDims({ w: width, h: Math.max(height, 80) });
        }
      }, 300);
    }
    return () => obs.disconnect();
  }, [entries.length]);

  // Wheel zoom handler
  const onWheel = (e) => {
    e.preventDefault();
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const PAD_L = 72;
    const gW2 = rect.width - PAD_L - 56;
    const mousePct = Math.max(0, Math.min(1, (e.clientX - rect.left - PAD_L) / gW2));
    const zoomFactor = e.deltaY < 0 ? 0.85 : 1.18;
    setZoomRange(([s, en]) => {
      const span = en - s;
      const newSpan = Math.min(1, Math.max(0.05, span * zoomFactor));
      const center = s + span * mousePct;
      const newS = Math.max(0, Math.min(center - newSpan * mousePct, 1 - newSpan));
      const newE = Math.min(1, newS + newSpan);
      return [newS, newE];
    });
  };

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  useEffect(() => {
    setAnimated(false);
    const t = setTimeout(() => setAnimated(true), 50);
    return () => clearTimeout(t);
  }, [currentPrice, entries.length]);

  useEffect(() => {
    if (!animated) return;
    [
      { ref: investedRef, delay: 0,   dur: 1200 },
      { ref: valueRef,    delay: 150, dur: 1400 },
      { ref: avgRef,      delay: 300, dur: 1100 },
    ].forEach(({ ref, delay, dur }) => {
      const el = ref.current;
      if (!el) return;
      const len = el.getTotalLength?.() || 2000;
      el.style.transition = "none";
      el.style.strokeDasharray = `${len}`;
      el.style.strokeDashoffset = `${len}`;
      el.style.opacity = "0";
      requestAnimationFrame(() => {
        setTimeout(() => {
          el.style.transition = `stroke-dashoffset ${dur}ms cubic-bezier(0.4,0,0.2,1), opacity 200ms ease`;
          el.style.strokeDashoffset = "0";
          el.style.opacity = "1";
        }, delay);
      });
    });
  }, [animated, dims]);

  if (!entries.length || !currentPrice) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", color:"var(--text5)", fontFamily:"Montserrat,sans-serif", fontSize:11, letterSpacing:"0.2em" }}>
      ENTER PRICE
    </div>
  );

  const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));
  let cumShares = 0, cumDollars = 0;
  const rawPoints = sorted.map((e) => {
    cumShares  += e.shares;
    cumDollars += e.dollars;
    return { date: e.date, cumShares, cumDollars };
  });
  const finalShares = cumShares || 1;
  const points = rawPoints.map(p => ({
    date:            p.date,
    value:           p.cumShares * currentPrice,
    invested:        p.cumDollars,
    avgCostPerShare: p.cumShares > 0 ? p.cumDollars / p.cumShares : 0,
    avgCostVal:      (p.cumShares > 0 ? p.cumDollars / p.cumShares : 0) * finalShares,
  }));

  // For Y scale use only value & invested so avg cost line doesn't distort chart
  const today = new Date().toISOString().split("T")[0];
  if (points.length && points[points.length - 1].date !== today)
    points.push({ ...points[points.length - 1], date: today });

  // Apply zoom
  const totalPts = points.length;
  const zStart = Math.floor(zoomRange[0] * (totalPts - 1));
  const zEnd   = Math.ceil(zoomRange[1] * (totalPts - 1));
  const visiblePoints = points.slice(zStart, zEnd + 1);

  // Guard: need at least 1 point to render
  if (!visiblePoints.length) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", color:"var(--text5)", fontFamily:"Montserrat,sans-serif", fontSize:11, letterSpacing:"0.2em" }}>
      LOADING...
    </div>
  );

  const PAD = { t:24, r:56, b:38, l:72 };
  const W = dims.w, H = dims.h;
  const gW = W - PAD.l - PAD.r, gH = H - PAD.t - PAD.b;
  if (gW <= 0 || gH <= 0) return null;

  const allVals = visiblePoints.flatMap(p => [p.value, p.invested]).filter(v => isFinite(v) && !isNaN(v));
  const minV = allVals.length ? Math.min(...allVals) * 0.92 : 0;
  const maxV = allVals.length ? Math.max(...allVals) * 1.06 : 1;
  const rangeV = (maxV - minV) || 1;
  const xS = (i) => PAD.l + (i / Math.max(visiblePoints.length - 1, 1)) * gW;
  const yS = (v) => {
    if (!isFinite(v) || isNaN(v)) return PAD.t + gH;
    const raw = PAD.t + gH - ((v - minV) / rangeV) * gH;
    return Math.max(PAD.t, Math.min(PAD.t + gH, raw));
  };

  // Separate Y scale for avg cost per share (price axis)
  const avgPrices = visiblePoints.map(p => p.avgCostPerShare).filter(v => v > 0 && isFinite(v));
  const minA = 0;
  const maxA = avgPrices.length ? Math.max(...avgPrices, 400) * 1.05 : 400;
  const rangeA = maxA - minA || 1;
  const yAvgS = (v) => {
    if (!isFinite(v) || isNaN(v)) return PAD.t + gH;
    const raw = PAD.t + gH - ((v - minA) / rangeA) * gH;
    return Math.max(PAD.t, Math.min(PAD.t + gH, raw));
  };

  const buildPath = (key) => {
    if (points.length === 1) return `M ${xS(0)} ${yS(visiblePoints[0][key])}`;
    let d = `M ${xS(0)} ${yS(visiblePoints[0][key])}`;
    for (let i = 1; i < visiblePoints.length; i++) {
      const cpx = (xS(i-1) + xS(i)) / 2;
      d += ` C ${cpx} ${yS(visiblePoints[i-1][key])}, ${cpx} ${yS(visiblePoints[i][key])}, ${xS(i)} ${yS(visiblePoints[i][key])}`;
    }
    return d;
  };

  // Avg cost path uses its own Y scale
  // Avg cost colored segments — darker purple at higher price, lighter at lower
  // Color maps $50 brackets: e.g. 0-50 lightest, 350-400 darkest
  const avgColorForPrice = (price) => {
    const clamp = Math.max(50, Math.min(500, price));
    const t = (clamp - 50) / 450; // 0=cheapest(light), 1=expensive(dark)
    const lightness = Math.round(75 - t * 40);
    return `hsl(270,60%,${lightness}%)`;
  };

  const buildAvgSegments = () => {
    const pts = visiblePoints.filter(p => p.avgCostPerShare > 0);
    if (pts.length < 2) return [];
    const segments = [];
    // Group consecutive points into $50 brackets
    let segStart = 0;
    const bracket = (p) => Math.floor(p.avgCostPerShare / 50);
    for (let i = 1; i <= pts.length; i++) {
      if (i === pts.length || bracket(pts[i]) !== bracket(pts[segStart])) {
        // Build path for this segment (include one extra point for continuity)
        const seg = pts.slice(segStart, Math.min(i + 1, pts.length));
        let d = `M ${xS(points.indexOf(seg[0]))} ${yAvgS(seg[0].avgCostPerShare)}`;
        for (let j = 1; j < seg.length; j++) {
          const iA = points.indexOf(seg[j]);
          const iB = points.indexOf(seg[j-1]);
          const cpx = (xS(iB) + xS(iA)) / 2;
          d += ` C ${cpx} ${yAvgS(seg[j-1].avgCostPerShare)}, ${cpx} ${yAvgS(seg[j].avgCostPerShare)}, ${xS(iA)} ${yAvgS(seg[j].avgCostPerShare)}`;
        }
        segments.push({ d, color: avgColorForPrice(pts[segStart].avgCostPerShare) });
        segStart = i;
      }
    }
    return segments;
  };

  // Build single avg path for gradient rendering
  const buildAvgPath = () => {
    const pts = visiblePoints.filter(p => p.avgCostPerShare > 0);
    if (pts.length < 2) return "";
    let d = `M ${xS(visiblePoints.indexOf(pts[0]))} ${yAvgS(pts[0].avgCostPerShare)}`;
    for (let i = 1; i < pts.length; i++) {
      const iA = visiblePoints.indexOf(pts[i]);
      const iB = visiblePoints.indexOf(pts[i-1]);
      const cpx = (xS(iB) + xS(iA)) / 2;
      d += ` C ${cpx} ${yAvgS(pts[i-1].avgCostPerShare)}, ${cpx} ${yAvgS(pts[i].avgCostPerShare)}, ${xS(iA)} ${yAvgS(pts[i].avgCostPerShare)}`;
    }
    return d;
  };

  // Compute gradient stops based on avg price at each point
  // Map price to lightness: high price = dark (35%), low price = light (75%)
  const avgGradientStops = (() => {
    const pts = visiblePoints.filter(p => p.avgCostPerShare > 0);
    if (pts.length < 2) return [];
    const prices = pts.map(p => p.avgCostPerShare);
    const maxP = Math.max(...prices);
    const minP = Math.min(...prices);
    const range = maxP - minP || 1;
    // Sample ~8 stops evenly
    const count = Math.min(pts.length, 8);
    return Array.from({ length: count }, (_, i) => {
      const idx = Math.round((i / (count - 1)) * (pts.length - 1));
      const price = pts[idx].avgCostPerShare;
      const t = (price - minP) / range; // 0=cheapest(light), 1=most expensive(dark)
      const lightness = Math.round(75 - t * 40);
      const offset = Math.round((visiblePoints.indexOf(pts[idx]) / Math.max(points.length - 1, 1)) * 100);
      return { offset: `${offset}%`, color: `hsl(270,60%,${lightness}%)` };
    });
  })();

  const valuePath    = buildPath("value");
  const investedPath = buildPath("invested");
  const avgPath      = buildAvgPath();
  const avgSegments  = buildAvgSegments();


  const isProfit = visiblePoints[visiblePoints.length-1]?.value >= visiblePoints[visiblePoints.length-1]?.invested;
  const profitColor = "#5aad27";
  const lossColor   = C.red;
  const gainColor   = isProfit ? profitColor : lossColor;

  const yTicks  = Array.from({length:5}, (_,i) => minV + (rangeV/4)*i);
  const xLabels = [0, Math.floor((visiblePoints.length-1)/2), visiblePoints.length-1];

  // Milestones: mark when portfolio value first crosses key levels
  const MILESTONES = [50000, 100000, 250000, 500000];
  const milestones = MILESTONES.map(m => {
    const idx = visiblePoints.findIndex(p => p.value >= m);
    if (idx < 0 || yS(m) < PAD.t || yS(m) > PAD.t+gH) return null;
    return { m, x: xS(idx), y: yS(m), label: m >= 1000 ? `$${m/1000}k` : `$${m}` };
  }).filter(Boolean);

  const onMove = (e) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const idx = Math.max(0, Math.min(visiblePoints.length-1, Math.round(((e.clientX-rect.left-PAD.l)/gW)*(visiblePoints.length-1))));
    clearTimeout(tooltipHideTimer.current);
    setTooltip({ x:xS(idx), yV:yS(visiblePoints[idx].value), yI:yS(visiblePoints[idx].invested), yAvg:yAvgS(visiblePoints[idx].avgCostPerShare), pt:points[idx] });
    requestAnimationFrame(() => setTooltipVisible(true));
  };

  const pnl = tooltip ? tooltip.pt.value - tooltip.pt.invested : 0;
  const pnlPct = tooltip && tooltip.pt.invested > 0 ? (pnl / tooltip.pt.invested) * 100 : 0;

  return (
    <div ref={wrapRef} style={{ position:"relative", width:"100%", height:"100%" }}>
      <svg ref={svgRef} width="100%" height={H} viewBox={`0 0 ${W} ${H}`}
        onMouseMove={onMove}
        onMouseLeave={() => {
          setTooltipVisible(false);
          tooltipHideTimer.current = setTimeout(() => setTooltip(null), 500);
        }}
        style={{ display:"block", overflow:"visible" }}>
        

        {/* Grid lines */}
        {yTicks.map((v,i) => (
          <line key={i} x1={PAD.l} y1={yS(v)} x2={W-PAD.r} y2={yS(v)} stroke="var(--border)" strokeWidth="1"/>
        ))}
        {/* Y labels */}
        {yTicks.map((v,i) => (
          <text key={i} x={PAD.l-10} y={yS(v)+4} textAnchor="end" fill="var(--text5)"
            fontSize="9" fontFamily="Montserrat,sans-serif" fontWeight="700">
            {v >= 1000 ? `$${(v/1000).toFixed(0)}k` : `$${v.toFixed(0)}`}
          </text>
        ))}
        {/* X labels */}
        {xLabels.map(idx => (
          <text key={idx} x={xS(idx)} y={H-6} textAnchor="middle" fill="var(--text5)"
            fontSize="9" fontFamily="Montserrat,sans-serif" fontWeight="700">
            {formatDate(points[idx].date)}
          </text>
        ))}

        {/* Area fills */}


        {/* Lines */}
        <path ref={investedRef} d={investedPath} fill="none"
          stroke="var(--text4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{opacity:0}}/>
        {/* Avg cost gradient definition */}
        <defs>
          <linearGradient id="avgCostGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            {avgGradientStops.map((s, i) => (
              <stop key={i} offset={s.offset} stopColor={s.color}/>
            ))}
          </linearGradient>
        </defs>

        {avgSegments.map((seg, i) => (
          <path key={i} ref={i === 0 ? avgRef : null} d={seg.d} fill="none"
            stroke={seg.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
            style={{opacity: i === 0 ? 0 : 1}}/>
        ))}
        <path ref={avgRef} d={avgPath} fill="none"
          stroke="url(#avgCostGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{opacity:0}}/>
        <path ref={valueRef} d={valuePath} fill="none"
          stroke={gainColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          style={{opacity:0}}/>

        {/* Avg cost right-side price markers */}
        {[0, 200, 300, 400].map(price => {
          const y = yAvgS(price);
          if (y < PAD.t || y > PAD.t + gH) return null;
          return (
            <g key={price}>
              <line x1={PAD.l} y1={y} x2={W - PAD.r} y2={y}
                stroke="#a78bfa" strokeWidth="0.5" strokeDasharray="3 4" opacity="0.3"/>
              <text x={W - PAD.r + 6} y={y + 4} fill="#a78bfa" fontSize="9"
                fontFamily="Montserrat,sans-serif" fontWeight="700" opacity="0.8">
                ${price}
              </text>
            </g>
          );
        })}

        {/* Milestone markers — rendered after lines so they sit on top */}
        {milestones.map(({m, x, y, label}) => (
          <g key={m}>
            <line x1={x} y1={PAD.t} x2={x} y2={PAD.t+gH} stroke="var(--border)" strokeWidth="1" strokeDasharray="3 3"/>
            <circle cx={x} cy={y} r="3" fill={C.orange}/>
            <rect x={x+5} y={y-24} width={label.length*6+6} height={14} rx="3" fill="var(--bg2)" stroke="var(--border)"/>
            <text x={x+8} y={y-14} fill={C.orange} fontSize="8" fontFamily="Montserrat,sans-serif" fontWeight="700">{label}</text>
          </g>
        ))}

        {/* Hover crosshair */}
        {tooltip && <>
          <line x1={tooltip.x} y1={PAD.t} x2={tooltip.x} y2={PAD.t+gH}
            stroke="var(--border2)" strokeWidth="1" strokeDasharray="4 3"/>
          <circle cx={tooltip.x} cy={tooltip.yV}   r="4.5" fill={gainColor}    stroke="#000" strokeWidth="1.5"/>
          <circle cx={tooltip.x} cy={tooltip.yI}   r="3.5" fill="var(--text4)" stroke="#000" strokeWidth="1.5"/>
          <circle cx={tooltip.x} cy={tooltip.yAvg} r="3.5" fill="#a78bfa"      stroke="#000" strokeWidth="1.5"/>
        </>}
      </svg>

      {/* Legend */}
      <div style={{ position:"absolute", top:6, right:16, display:"flex", gap:16, alignItems:"center" }}>
        {/* Zoom indicator */}
        {(zoomRange[0] > 0.001 || zoomRange[1] < 0.999) && (
          <button onClick={() => setZoomRange([0,1])} style={{
            background:"rgba(242,135,5,0.1)", border:`1px solid rgba(242,135,5,0.4)`,
            color:C.orange, borderRadius:6, padding:"2px 8px", cursor:"pointer",
            fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, letterSpacing:"0.1em"
          }}>
            {Math.round((zoomRange[1]-zoomRange[0])*100)}% · RESET
          </button>
        )}
        {!(zoomRange[0] > 0.001 || zoomRange[1] < 0.999) && (
          <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, color:"var(--text5)", letterSpacing:"0.1em" }}>
            scroll to zoom
          </span>
        )}
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <div style={{ width:14, height:2.5, borderRadius:2, background:gainColor }}/>
          <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"var(--text3)", letterSpacing:"0.12em" }}>VALUE</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <div style={{ width:14, height:2, borderRadius:2, background:"var(--text4)" }}/>
          <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"var(--text4)", letterSpacing:"0.12em" }}>INVESTED</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <svg width="16" height="4">
            <defs>
              <linearGradient id="avgGradLegend" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="hsl(270,60%,35%)"/>
                <stop offset="100%" stopColor="hsl(270,60%,75%)"/>
              </linearGradient>
            </defs>
            <line x1="0" y1="2" x2="16" y2="2" stroke="url(#avgGradLegend)" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
          <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, color:"#a78bfa", letterSpacing:"0.12em" }}>AVG COST</span>
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && (() => {
        const TOOLTIP_H = 130;
        const topAbove = tooltip.yV - TOOLTIP_H - 16;
        const top = topAbove < 4 ? 4 : topAbove;
        return (
        <div style={{
          position:"absolute",
          left: Math.min(Math.max(tooltip.x - 80, 0), dims.w - 170),
          top,
          background:"var(--bg2)",
          border:`1px solid ${gainColor}`,
          borderRadius:8, padding:"10px 16px",
          pointerEvents:"none", fontFamily:"Montserrat,sans-serif", zIndex:10,
          opacity: tooltipVisible ? 1 : 0,
          transition: tooltipVisible ? "opacity 0.33s ease" : "opacity 0.5s ease",
        }}>
          <div style={{ fontSize:9, color:"var(--text3)", letterSpacing:"0.2em", marginBottom:8 }}>
            {formatDate(tooltip.pt.date)}
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            <div style={{ display:"flex", justifyContent:"space-between", gap:20 }}>
              <span style={{ fontSize:10, color:"var(--text2)" }}>Value</span>
              <span style={{ fontSize:12, fontWeight:700, color:gainColor }}>{fmt$(tooltip.pt.value)}</span>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", gap:20 }}>
              <span style={{ fontSize:10, color:"var(--text2)" }}>Invested</span>
              <span style={{ fontSize:12, fontWeight:700, color:"var(--text2)" }}>{fmt$(tooltip.pt.invested)}</span>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", gap:20 }}>
              <span style={{ fontSize:10, color:"#a78bfa" }}>Avg Cost</span>
              <span style={{ fontSize:12, fontWeight:700, color:"#a78bfa" }}>{fmt$(tooltip.pt.avgCostPerShare)}/share</span>
            </div>
            <div style={{ marginTop:5, paddingTop:6, borderTop:"1px solid var(--border)", display:"flex", justifyContent:"space-between", gap:20 }}>
              <span style={{ fontSize:10, color:"var(--text2)" }}>P&L</span>
              <span style={{ fontSize:12, fontWeight:700, color: pnl >= 0 ? profitColor : lossColor }}>
                {pnl >= 0 ? "+" : ""}{fmt$(pnl)} ({pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(1)}%)
              </span>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

export { ChartContainer, PortfolioChart };
